import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
  signal,
} from '@angular/core';
import {
  TrialInviteDialogContainerComponent,
  TrialStatusButtonComponent,
  TrialWidgetComponent,
} from '@bizzdesign/feature-trial-experience';
import {
  ActionButtonDirective,
  PreventScrollDirective,
} from '@bizzdesign/shared/directives';
import { FeatureFlagsStore } from '@bizzdesign/shared/feature-flags';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import {
  AccountToolbarSettings,
  type BDMenuItem,
  type ToolbarButton,
} from '@bizzdesign/shared/models';
import { TranslateActionButtonPipe } from '@bizzdesign/shared/pipes';
import { AccountToolbarService } from '@bizzdesign/shared/services';
import { ActionStatePipe, type BdAvatar } from '@bizzdesign/shared/utils';
import { AccountMenuComponent } from '@bizzdesign/ui-account-menu';
import { ClippyPeekDirective } from '@bizzdesign/ui-components';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { delay, type Observable } from 'rxjs';
import { AccountNotificationsComponent } from '../account-notifications/account-notifications.component';
import { AvatarGroupMenuComponent } from '../avatar-group-menu/avatar-group-menu.component';

@Component({
  selector: 'bd-account-toolbar',
  templateUrl: './account-toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    AccountNotificationsComponent,
    AccountMenuComponent,
    ButtonModule,
    SharedI18nModule,
    AvatarGroupMenuComponent,
    TranslateActionButtonPipe,
    ActionStatePipe,
    ActionButtonDirective,
    TooltipModule,
    PreventScrollDirective,
    TrialWidgetComponent,
    TrialStatusButtonComponent,
    TrialInviteDialogContainerComponent,
    ClippyPeekDirective,
  ],
})
export class AccountToolbarComponent {
  readonly accountToolbarService = inject(AccountToolbarService);
  readonly featureFlags = inject(FeatureFlagsStore);

  @Input() accountNotificationsItems: BDMenuItem[] = [];
  @Input() userAvatar: BdAvatar | null = null;
  @Input() accountMenuItems: BDMenuItem[] = [];
  @Input() accountToolbarSettings: AccountToolbarSettings =
    new AccountToolbarSettings();
  @Input() showClippy = false;
  @Input() moveClippyEyebrows = false;

  readonly inviteDialogVisible = signal(false);

  readonly toolbarSecondaryButton$: Observable<ToolbarButton | undefined> =
    this.accountToolbarService.activeToolbarSecondaryButton$.pipe(delay(0));
  readonly toolbarPrimaryButton$: Observable<ToolbarButton | undefined> =
    this.accountToolbarService.toolbarPrimaryButton$.pipe(delay(0));
  readonly avatars$: Observable<BdAvatar[] | undefined> =
    this.accountToolbarService.activeAvatars$.pipe(delay(0));
  readonly showMinimal$: Observable<boolean> =
    this.accountToolbarService.activeShowMinimal$.pipe(delay(0));
}
